import type VectorSource from 'ol/source/Vector';
import type WKBObject from 'ol/format/WKB';
import type { Projection as OLProjection } from 'ol/proj';

import type { WkbLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/wkb-layer-entry-config';
import { AbstractGVVector } from '@/geo/layer/gv-layers/vector/abstract-gv-vector';
import { GeoUtilities } from '@/geo/utils/utilities';
import { logger } from '@/core/utils/logger';

/**
 * Manages a WKB layer.
 */
export class GVWKB extends AbstractGVVector {
  /** Custom source features */
  #wkbSource: WKBObject | undefined;

  /**
   * Constructs a GVWKB layer to manage an OpenLayer layer.
   *
   * @param olSource - The OpenLayer source.
   * @param layerConfig - The layer configuration.
   */
  // The constructor is not useless, it narrows down the accepted parameter type.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(olSource: VectorSource, layerConfig: WkbLayerEntryConfig) {
    super(olSource, layerConfig);
  }

  // #region OVERRIDES

  /**
   * Overrides the parent class's getter to provide a more specific return type (covariant return).
   *
   * @returns The strongly-typed layer configuration specific to this layer.
   */
  override getLayerConfig(): WkbLayerEntryConfig {
    // Call parent and cast
    return super.getLayerConfig() as WkbLayerEntryConfig;
  }

  /**
   * Overrides the refresh to reload the WKB object in the layer source once the refresh completes.
   *
   * @param projection - Optional projection to refresh to.
   */
  override onRefresh(projection: OLProjection | undefined): void {
    // Sure
    super.onRefresh(projection);

    // If projection is defined
    if (projection) {
      // After a refresh, reload the WKB Source if any
      this.updateWkbSource(projection).catch((error: unknown) => {
        // Log promise failed
        logger.logPromiseFailed('in updateWkbSource in GV-WKB.onRefresh()', error);
      });
    }
  }

  // #endregion OVERRIDES

  // #region METHODS

  /**
   * Loads a WKB object as the layer source features, overriding the current features if any.
   *
   * @param wkb - The WKB object.
   * @param projection - The output projection.
   * @returns A promise that resolves once the source has been updated with the new features.
   */
  async setWkbSource(wkb: WKBObject | string, projection: OLProjection): Promise<void> {
    // Convert string to JSON if necessary
    const wkbObject = typeof wkb === 'string' ? JSON.parse(wkb) : wkb;

    // Keep internally
    this.#wkbSource = wkbObject;

    // Read the features
    const sourceFeatureInfo = await GeoUtilities.readFeaturesFromWKB(wkbObject, this.getDataProjection(), projection);

    // Get the OL layer
    const olLayer = this.getOLLayer();

    // Remove current features and add new ones
    olLayer.getSource()?.clear();

    // If has features to add
    if (sourceFeatureInfo.features.length) {
      olLayer.getSource()?.addFeatures(sourceFeatureInfo.features);
    }

    // Refresh
    olLayer.changed();
  }

  /**
   * Updates the WKB object, if any, to reproject the features into the new provided projection.
   *
   * @param projection - The projection to project the wkb source features into.
   * @returns A promise that resolves once the source has been updated with the reprojected features, or immediately if no wkb source is defined.
   */
  updateWkbSource(projection: OLProjection): Promise<void> {
    // If a custom geojson is defined
    if (this.#wkbSource) {
      // Redirect
      return this.setWkbSource(this.#wkbSource, projection);
    }

    // Nothing to do
    return Promise.resolve();
  }

  // #endregion METHODS
}
